import * as React from 'react';
import { useRedirect, useNotify } from 'react-admin';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Stack,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { MemberRole } from '../../enums/MemberRole';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { TextField as MuiTextField, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MemberService } from '../../services/member/memberService';
import { VehicleType } from '../../enums/VehicleType';
import CourierService from '../../services/courier/courierService';
import { LocationService } from '../../services/location/locationService';
import { ChangeMemberPasswordDialog } from '../dialogs/ChangeMemberPasswordDialog';
import { PageHeader } from '../page-header/PageHeader';
import { FormBackButton } from '../back-button/FormBackButton';
import { IconButton } from '../icon-button/IconButton';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import { cardActionButtonSx } from '../../core/theme/buttonStyles';
import { ScrollToFirstError } from '../form/ScrollToFirstError';
import * as Sentry from '@sentry/react';
import { isAdminRole, hasRole } from '../../utils/roles';

const ADMIN_ONLY_ROLES = ['ADMIN', 'VENDOR', 'RESTAURANT_OWNER'];
const RESTAURANT_OWNER_HIDDEN_ROLES = ['ADMIN', 'RESTAURANT_OWNER'];
const VEHICLE_TYPE_CHOICES = Object.entries(VehicleType).map(([id, name]) => ({ id, name }));

export const tableShellSx = {
  border: '1px solid var(--color-secondary)',
  borderRadius: 'var(--radius-xs)',
  backgroundColor: 'var(--color-surface)',
  overflow: 'hidden',
  mx: 'var(--space-6)',
};

export const headCellSx = {
  paddingLeft: 'var(--space-6)',
  py: 'var(--space-3)',
  fontSize: 'var(--text-xs)',
  fontWeight: 700,
};

export const bodyCellSx = {
  py: 'var(--space-3)',
  paddingLeft: 'var(--space-6)',
  fontSize: 'var(--text-xs)',
};

export const inputSx = {
  '& .MuiOutlinedInput-root': {
    border: '1px solid var(--color-secondary)',
    borderRadius: 'var(--radius-xs)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
};

const headerTitleSx = {
  paddingLeft: 'var(--space-6)',
  fontWeight: 700,
  fontSize: 'var(--text-2xl)',
};

interface MembersFormContentProps {
  mode: 'create' | 'edit';
  record?: any;
  couriers?: any[];
}

// Seeds the first location into form state once the vendor/owner field becomes
// active, so an untouched dropdown submits a real locationId instead of ''
// (stored as null on the backend). Powstalo pod natywny <select>, ktory pokazywal
// pierwsza opcje, choc stan Formika byl pusty. Select MUI renderuje w tej sytuacji
// puste pole, ale domyslny wybor zostaje: bez niego formularz nadal wyslalby
// pusta lokalizacje, tyle ze teraz wygladajac na niewypelniony.
const LocationDefaultSync: React.FC<{ locations: any[]; active: boolean }> = ({
  locations,
  active,
}) => {
  const { values, setFieldValue } = useFormikContext<{ location: any }>();
  React.useEffect(() => {
    if (active && locations.length > 0 && !values.location) {
      setFieldValue('location', locations[0].id);
    }
  }, [active, locations, values.location, setFieldValue]);
  return null;
};

export const MembersFormContent: React.FC<MembersFormContentProps> = ({
  mode,
  record,
  couriers = [],
}) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { t } = useTranslation();

  const memberService = new MemberService();
  const courierService = new CourierService();
  const locationService = new LocationService();

  const [isCourier, setIsCourier] = React.useState(false);
  const [isVendor, setIsVendor] = React.useState(false);
  const [isRestaurantOwner, setIsRestaurantOwner] = React.useState(false);
  const [locations, setLocations] = React.useState<any[]>([]);
  const [openPasswordDialog, setOpenPasswordDialog] = React.useState(false);

  const isAdmin = isAdminRole();
  const isRestaurantOwnerUser = hasRole('RESTAURANT_OWNER');

  const ALL_MEMBER_CHOICES = Object.keys(MemberRole).map((id) => ({
    id,
    name: t(`enums.memberRole.${id}`, { defaultValue: MemberRole[id as keyof typeof MemberRole] }),
  }));

  const MEMBER_CHOICES = isAdmin
    ? ALL_MEMBER_CHOICES
    : isRestaurantOwnerUser
      ? ALL_MEMBER_CHOICES.filter((c) => !RESTAURANT_OWNER_HIDDEN_ROLES.includes(c.id))
      : ALL_MEMBER_CHOICES.filter((c) => !ADMIN_ONLY_ROLES.includes(c.id));

  React.useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        const res = await locationService.getLocations();
        if (isActive) setLocations(res);
      } catch (e: any) {
        notify(e.response?.data?.error ?? t('notifications.failedToLoadLocations'), {
          type: 'error',
        });
        Sentry.captureException(e);
      }
    })();
    return () => {
      isActive = false;
    };
  }, []);

  const _rawRole = record?.role ?? record?.roles?.[0] ?? '';
  const _roleKey =
    _rawRole && Object.prototype.hasOwnProperty.call(MemberRole, _rawRole)
      ? String(_rawRole)
      : (Object.entries(MemberRole).find(([, val]) => String(val) === String(_rawRole))?.[0] ?? '');

  const courier =
    mode === 'edit' && _roleKey === 'COURIER'
      ? couriers.find(
        (c: any) => String(c.memberId ?? c.userId ?? c.id) === String(record?.id),
      )
      : undefined;

  React.useEffect(() => {
    if (mode === 'edit') {
      setIsCourier(_roleKey === 'COURIER');
      setIsVendor(_roleKey === 'VENDOR');
      setIsRestaurantOwner(_roleKey === 'RESTAURANT_OWNER');
    }
  }, [_roleKey, mode]);

  const validationSchema = Yup.object({
    role: Yup.string().required(t('validation.required')),
    vehicleType: Yup.string(),
    name: Yup.string().required(t('validation.required')),
    surname: Yup.string().required(t('validation.required')),
    email: Yup.string().email(t('validation.emailInvalid')).required(t('validation.required')),
    phone: Yup.string()
      .required(t('validation.phoneRequired'))
      .matches(/^\+[0-9]{2,4}[0-9]{5,12}$/, t('validation.phoneInvalid')),
    location: Yup.string().when('role', {
      is: (role: string) => role === 'VENDOR' || role === 'RESTAURANT_OWNER',
      then: (schema) => schema.required(t('validation.required')),
      otherwise: (schema) => schema,
    }),
  });

  const initialValues =
    mode === 'edit' && record
      ? {
        role: _roleKey,
        vehicleType: courier?.vehicleType ?? '',
        location: record?.locationId ?? '',
        name: (record?.name ?? '').split(' ')[0],
        surname: (record?.name ?? '').split(' ')[1] ?? '',
        email: record?.email ?? '',
        phone: record?.phoneNumber ?? record?.phone ?? '',
      }
      : {
        // Native <select> shows the first role visually while Formik state
        // stays ''; seed it so an untouched dropdown still submits a role.
        role: MEMBER_CHOICES[0]?.id ?? '',
        vehicleType: '',
        name: '',
        surname: '',
        email: '',
        phone: '',
        location: '',
      };

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
    try {
      if (mode === 'create') {
        if (isCourier) {
          await memberService.createCourier({
            vehicleType: values.vehicleType,
            userRecordRequest: {
              email: values.email,
              phoneNumber: values.phone ?? '',
              name: [values.name, values.surname].join(' ').trim(),
            },
          });
        } else if (isVendor || isRestaurantOwner) {
          await memberService.createVendor({
            role: values.role,
            locationId: values.location,
            userRecordRequest: {
              email: values.email,
              phoneNumber: values.phone ?? '',
              name: [values.name, values.surname].join(' ').trim(),
            },
          });
        } else {
          await memberService.createMember({
            userRecordRequest: {
              email: values.email,
              phoneNumber: values.phone ?? '',
              name: [values.name, values.surname].join(' ').trim(),
            },
            role: values.role,
          });
        }
      } else {
        if (values.role === 'COURIER') {
          await courierService.updateCourier({
            courierId: record.id,
            phoneNumber: values.phone,
            name: values.name + ' ' + values.surname,
            vehicleType: values.vehicleType,
          });
        } else if (values.role === 'VENDOR') {
          await memberService.updateVendor({
            id: record.id,
            phoneNumber: values.phone,
            name: values.name + ' ' + values.surname,
            locationId: values.location,
          });
        } else {
          await memberService.updateMember({
            id: record.id,
            phoneNumber: values.phone,
            name: values.name + ' ' + values.surname,
          });
        }
      }
      notify(t('common.saved'), { type: 'success' });
      redirect('list', 'members');
    } catch (e: any) {
      notify(e.response.data.error, { type: 'error' });
      console.error(e.response.data.error);
      Sentry.captureException(e);
    } finally {
      setSubmitting(false);
    }
  };

  const isMobile = useMediaQuery('(max-width:600px)');

  const fieldSx = isMobile ? { ...inputSx } : { ...inputSx, width: '150px' };
  const fullFieldSx = inputSx;

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={mode === 'edit'}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
          <Form>
            <ScrollToFirstError />
            <LocationDefaultSync
              locations={locations}
              active={isVendor || isRestaurantOwner}
            />
            <PageHeader
              stretchActionsOnMobile
              back={<FormBackButton />}
              title={mode === 'edit' ? t('members.editMemberTitle') : t('members.addMemberTitle')}
              actions={
                mode === 'edit' ? (
                  <IconButton
                    icon={<LockResetOutlinedIcon />}
                    label={t('members.changePassword')}
                    sx={isMobile ? cardActionButtonSx : undefined}
                    onClick={() => setOpenPasswordDialog(true)}
                  />
                ) : undefined
              }
            />

            {isMobile ? (
              <Stack spacing={2} sx={{ px: 'var(--space-4)', pb: 'var(--space-4)' }}>
                <MuiTextField
                  select
                  disabled={mode === 'edit'}
                  name="role"
                  label={t('members.columns.role')}
                  sx={fullFieldSx}
                  size="small"
                  fullWidth
                  value={values.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    setFieldValue('role', role);
                    setIsCourier(role === 'COURIER');
                    setIsVendor(role === 'VENDOR');
                    setIsRestaurantOwner(role === 'RESTAURANT_OWNER');
                  }}
                  onBlur={handleBlur}
                  error={touched.role && Boolean(errors.role)}
                  helperText={touched.role ? errors.role || ' ' : ' '}
                >
                  {MEMBER_CHOICES.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </MuiTextField>

                {isCourier && (
                  <MuiTextField
                    select
                    name="vehicleType"
                    label={t('members.columns.typeOfVehicle')}
                    sx={fullFieldSx}
                    size="small"
                    fullWidth
                    value={values.vehicleType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.vehicleType && Boolean(errors.vehicleType)}
                    helperText={touched.vehicleType ? (errors.vehicleType as string) || ' ' : ' '}
                  >
                    {VEHICLE_TYPE_CHOICES.map((c) => (
                      <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                    ))}
                  </MuiTextField>
                )}

                {(isVendor || isRestaurantOwner) && (
                  <MuiTextField
                    select
                    name="location"
                    label={t('members.columns.location')}
                    sx={fullFieldSx}
                    size="small"
                    fullWidth
                    value={values.location}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.location && Boolean(errors.location)}
                    helperText={touched.location ? (errors.location as string) || ' ' : ' '}
                  >
                    {locations.map((loc: { id: number; name: string }) => (
                      <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                    ))}
                  </MuiTextField>
                )}

                <MuiTextField
                  name="name"
                  label={t('members.columns.name')}
                  sx={fullFieldSx}
                  size="small"
                  fullWidth
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name ? (errors.name as string) || ' ' : ' '}
                />

                <MuiTextField
                  name="surname"
                  label={t('members.columns.surname')}
                  sx={fullFieldSx}
                  size="small"
                  fullWidth
                  value={values.surname}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.surname && Boolean(errors.surname)}
                  helperText={touched.surname ? (errors.surname as string) || ' ' : ' '}
                />

                <MuiTextField
                  disabled={mode === 'edit'}
                  name="email"
                  type="email"
                  label={t('members.columns.email')}
                  sx={fullFieldSx}
                  size="small"
                  fullWidth
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email ? (errors.email as string) || ' ' : ' '}
                />

                <MuiTextField
                  name="phone"
                  label={t('members.columns.phone')}
                  sx={fullFieldSx}
                  size="small"
                  fullWidth
                  value={values.phone}
                  onChange={handleChange}
                  onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                  onBlur={handleBlur}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone ? (errors.phone as string) || ' ' : ' '}
                />
              </Stack>
            ) : (
              <Box sx={tableShellSx}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>{t('members.columns.role')}</TableCell>
                      {isCourier && (
                        <TableCell sx={headCellSx}>{t('members.columns.typeOfVehicle')}</TableCell>
                      )}
                      {(isVendor || isRestaurantOwner) && (
                        <TableCell sx={headCellSx}>{t('members.columns.location')}</TableCell>
                      )}
                      <TableCell sx={headCellSx}>{t('members.columns.name')}</TableCell>
                      <TableCell sx={headCellSx}>{t('members.columns.surname')}</TableCell>
                      <TableCell sx={headCellSx}>{t('members.columns.email')}</TableCell>
                      <TableCell sx={headCellSx}>{t('members.columns.phone')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={bodyCellSx}>
                        <MuiTextField
                          select
                          disabled={mode === 'edit'}
                          name="role"
                          label=""
                          sx={fieldSx}
                          size="small"
                          fullWidth
                          value={values.role}
                          onChange={(e) => {
                            const role = e.target.value;
                            setFieldValue('role', role);
                            setIsCourier(role === 'COURIER');
                            setIsVendor(role === 'VENDOR');
                            setIsRestaurantOwner(role === 'RESTAURANT_OWNER');
                          }}
                          onBlur={handleBlur}
                          error={touched.role && Boolean(errors.role)}
                          helperText={touched.role ? errors.role || ' ' : ' '}
                        >
                          {MEMBER_CHOICES.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                          ))}
                        </MuiTextField>
                      </TableCell>
                      {isCourier && (
                        <TableCell sx={bodyCellSx}>
                          <MuiTextField
                            select
                            name="vehicleType"
                            label=""
                            sx={inputSx}
                            size="small"
                            fullWidth
                            placeholder={t('members.columns.typeOfVehicle')}
                            value={values.vehicleType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.vehicleType && Boolean(errors.vehicleType)}
                            helperText={touched.vehicleType ? (errors.vehicleType as string) || ' ' : ' '}
                          >
                            {VEHICLE_TYPE_CHOICES.map((c) => (
                              <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                            ))}
                          </MuiTextField>
                        </TableCell>
                      )}
                      {(isVendor || isRestaurantOwner) && (
                        <TableCell sx={bodyCellSx}>
                          <MuiTextField
                            select
                            name="location"
                            label=""
                            sx={{ ...inputSx, width: '150px' }}
                            size="small"
                            fullWidth
                            value={values.location}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.location && Boolean(errors.location)}
                            helperText={touched.location ? (errors.location as string) || ' ' : ' '}
                          >
                            {locations.map((loc: { id: number; name: string }) => (
                              <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                            ))}
                          </MuiTextField>
                        </TableCell>
                      )}
                      <TableCell sx={bodyCellSx}>
                        <MuiTextField
                          name="name"
                          label=""
                          sx={inputSx}
                          size="small"
                          fullWidth
                          placeholder={t('members.columns.name')}
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name ? (errors.name as string) || ' ' : ' '}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <MuiTextField
                          name="surname"
                          label=""
                          sx={inputSx}
                          size="small"
                          fullWidth
                          placeholder={t('members.columns.surname')}
                          value={values.surname}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.surname && Boolean(errors.surname)}
                          helperText={touched.surname ? (errors.surname as string) || ' ' : ' '}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <MuiTextField
                          disabled={mode === 'edit'}
                          name="email"
                          type="email"
                          label=""
                          sx={inputSx}
                          size="small"
                          fullWidth
                          placeholder={t('members.columns.email')}
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email ? (errors.email as string) || ' ' : ' '}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <MuiTextField
                          name="phone"
                          label=""
                          sx={inputSx}
                          size="small"
                          fullWidth
                          placeholder={t('members.columns.phone')}
                          value={values.phone}
                          onChange={handleChange}
                          onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                          onBlur={handleBlur}
                          error={touched.phone && Boolean(errors.phone)}
                          helperText={touched.phone ? (errors.phone as string) || ' ' : ' '}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                /* Phones: primary action on top, full width. Desktop keeps the
                   conventional cancel-left / submit-right pair, which is why
                   the row is reversed instead of reordering the DOM. */
                flexDirection: { xs: 'column', md: 'row-reverse' },
                justifyContent: { xs: 'stretch', md: 'flex-start' },
                gap: { xs: 'var(--space-3)', md: 2 },
                mt: 2,
                mb: 2,
                px: isMobile ? 'var(--space-4)' : 'var(--space-6)',
              }}
            >
              <Button
                type="submit"
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} sx={{ color: 'var(--color-text-inverse)' }} />
                  ) : undefined
                }
                sx={{
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-inverse)',
                  boxShadow: 'none',
                  transition: 'background-color 200ms ease',
                  '&:hover': {
                    backgroundColor: 'var(--color-primary-hover)',
                    color: 'var(--color-text-inverse)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-text-inverse)',
                    opacity: 0.7,
                  },
                }}
              >
                {mode === 'create' ? t('common.add') : t('common.saveChanges')}
              </Button>

              <Button
                type="button"
                variant="text"
                onClick={() => redirect('list', 'members')}
                sx={{
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--color-text-secondary)',
                  '&:hover': {
                    backgroundColor: 'var(--color-bg-subtle)',
                    color: 'var(--color-text-primary)',
                  },
                }}
              >
                {t('common.cancel')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      {mode === 'edit' && (
        <ChangeMemberPasswordDialog
          open={openPasswordDialog}
          onClose={() => setOpenPasswordDialog(false)}
          userId={record?.id}
        />
      )}
    </>
  );
};
